'use strict';
export class RDDL {
    /**
     * Constructor
     *
     * @param Object config
     */
    constructor() {
        /**
         * Whether form entry has started
         *
         * @type Boolean
         */
        this.form_started = false;
        /**
         * dataLayer array
         *
         * @type array
         */
        window.dataLayer = window.dataLayer || [];
        /**
         * Define global metadata
         *
         * @type Object
         */
        this.globals = {
            author: document.querySelector('meta[name="author"]') ? document.querySelector('meta[name="author"]').content : '',
            page_category: '',
            page_section: '',
            page_template: [...document.body.classList].filter(classname => classname.startsWith('page-template') || classname.startsWith('single')).join(' '),
            page_title: document.title || '',
            published_date: document.querySelector('meta[itemprop="datePublished"]') ? document.querySelector('meta[itemprop="datePublished"]').content : ''
        };
    }
    /**
     * Init
     *
     * @param Object config
     */
    init(config = {}) {
        /**
         * Set config
         *
         * @type Object
         */
        this.config = Object.assign({
            /**
             * Client ID
             *
             * @type String
             */
            client_id: '',
            /**
             * Component classname
             *
             * @type String
             */
            component_class: '',
            /**
             * Footer ID
             *
             * @type String
             */
            footer_id: '',
            /**
             * Form class
             *
             * @type String
             */
            form_class: '',
            /**
             * Header ID
             *
             * @type String
             */
            header_id: '',
            /**
             * Uses Marketo forms
             *
             * @type Boolean
             */
            is_marketo: true,
            /**
             * Is testing
             *
             * @type Boolean
             */
            is_testing: false,
            /**
             * Login status
             *
             * Indicate prospect vs. non-prospect user
             *
             * @type Boolean
             */
            is_logged_in: false,
            /**
             * Main body ID
             *
             * @type String
             */
            main_body_id: '',
            /**
             * Marketo Munchkin ID
             *
             * @type String
             */
            munchkin_id: '',
            /**
             * Segment ID
             *
             * @type String
             */
            segment_id: '',
            /**
             * Site URL constructor
             *
             * @type URL
             */
            site: new URL(window.location.href),
            /**
             * User ID
             *
             * @type String
             */
            user_id: ''
        }, config);
        if (this.config.is_testing) {
            console.log(this.config);
        }
        // Attach listeners
        this._init_header_footer_clicks('header');
        this._init_header_footer_clicks('footer');
        this._init_body_clicks();
        this._init_form_start();
        this._init_form_submit();
        // Push global page dataLayer
        window.dataLayer.push(this.global_page);
    }
    /**
     * Init header, footer clicks
     *
     * @param  String section header|footer
     * @return void
     */
    _init_header_footer_clicks(section) {
        let event_name = 'menu_click', id = (section == 'header') ? this.config.header_id : this.config.footer_id, location = (section == 'header') ? 'top menu' : 'footer';
        if (document.getElementById(id) !== null) {
            [...document.getElementById(id).getElementsByTagName('a')].forEach((link) => {
                link.addEventListener('click', (e) => {
                    const target = e.target;
                    const parent = target.closest('a');
                    let link_text, link_url, link_id;
                    if (target.nodeName == 'A') {
                        link_text = target.innerText;
                        link_url = target.href;
                    }
                    else if (target.nodeName == 'IMG') {
                        link_text = e.target.alt;
                        link_url = parent.href;
                    }
                    else {
                        link_text = parent.innerText;
                        link_url = parent.href;
                    }
                    let obj = {
                        'event': event_name,
                        'menu_id': link_id,
                        'link_text': link_text,
                        'link_url': link_url,
                        'link_location': location,
                    };
                    window.dataLayer.push(obj);
                    if (this.config.is_testing) {
                        e.preventDefault();
                        console.log(obj);
                    }
                });
            });
        }
    }
    /**
     * Init main body clicks
     *
     * @return void
     */
    _init_body_clicks() {
        if (document.getElementById(this.config.main_body_id) !== null) {
            [...document.getElementById(this.config.main_body_id).getElementsByTagName('a')].forEach((link) => {
                link.addEventListener('click', (e) => {
                    const target = e.target;
                    const parent = target.closest('a');
                    // Link must have href
                    if (!target.href && !parent.href)
                        return;
                    let component = link.closest(this.config.component_class), link_text, link_url, link_id;
                    if (target.nodeName == 'A') {
                        link_text = target.innerText;
                        link_url = target.href;
                    }
                    else if (target.nodeName == 'IMG') {
                        link_text = e.target.alt;
                        link_url = parent.href;
                    }
                    else {
                        link_text = parent.innerText;
                        link_url = parent.href;
                    }
                    // Get URL constructor for hostname
                    let url = (link_url) ? new URL(link_url) : new URL(window.location.href);
                    let obj = {
                        'event': (url.host == this.config.site.host) ? 'internal_link_click' : 'external_link_click',
                        'link_id': link_id,
                        'link_text': link_text,
                        'link_url': link_url,
                        'link_location': component === null || component === void 0 ? void 0 : component.id,
                    };
                    window.dataLayer.push(obj);
                    if (this.config.is_testing) {
                        e.preventDefault();
                        console.log(obj);
                    }
                });
            });
        }
    }
    /**
     * Init form start event
     *
     * @return void
     */
    _init_form_start() {
        let _self = this;
        document.querySelectorAll('form.' + this.config.form_class).forEach((form) => {
            /**
             * Marketo form
             *
             */
            if (_self.config.is_marketo) {
                if (typeof window.MktoForms2 !== 'undefined' && window.MktoForms2.whenReady) {
                    window.MktoForms2.whenReady(function (marketo_form) {
                        let obj = {
                            'event': 'form_start',
                            'mkto_form_id': marketo_form.getId(),
                            'form_name': form.dataset.formTitleValue,
                            'form_url': _self.config.site.href,
                            'form_field': '',
                            'user_id': '',
                            'client_id': _self.config.client_id,
                            'munchkin_id': _self.config.munchkin_id
                        };
                        // Attach events to fields
                        form.querySelectorAll('.mktoField').forEach((field) => {
                            field.addEventListener('keydown', (event) => {
                                if (event.isComposing ||
                                    event.keyCode === 229 ||
                                    _self.form_started === true) {
                                    return;
                                }
                                // Attach unload event when form has been entered
                                _self._init_form_exit_marketo(form, marketo_form);
                                obj.form_field = field.name;
                                window.dataLayer.push(obj);
                                _self.form_started = true;
                                if (_self.config.is_testing) {
                                    console.log(obj);
                                }
                            });
                        });
                    });
                }
            }
            /**
             * Non-Marketo form
             *
             */
            else {
                let obj = {
                    'event': 'form_start',
                    'mkto_form_id': '',
                    'form_name': form.dataset.title,
                    'form_url': _self.config.site.href,
                    'form_field': '',
                    'user_id': '',
                    'client_id': _self.config.client_id,
                    'munchkin_id': _self.config.munchkin_id
                };
                // Attach events to fields
                form.querySelectorAll('input').forEach((field) => {
                    field.addEventListener('keydown', (event) => {
                        if (event.isComposing ||
                            event.keyCode === 229 ||
                            _self.form_started === true) {
                            return;
                        }
                        // Attach unload event when form has been entered
                        _self._init_form_exit(form);
                        obj.form_field = field.name;
                        window.dataLayer.push(obj);
                        _self.form_started = true;
                        if (_self.config.is_testing) {
                            console.log(obj);
                        }
                    });
                });
            }
        });
    }
    /**
     * Init form beforeunload event on form enter
     *
     * For non-Marketo forms
     *
     * @param HTMLFormElement form
     */
    _init_form_exit(form) {
        let _self = this, obj = {
            'event': 'form_exit',
            'mkto_form_id': '',
            'form_name': form.dataset.title,
            'form_url': _self.config.site.href,
            'form_field': '',
            'user_id': '',
            'client_id': _self.config.client_id,
            'munchkin_id': _self.config.munchkin_id
        };
        addEventListener('beforeunload', () => {
            let form_elements = Array.from(form.elements);
            /**
             * Get all non-empty fields and map keys to string
             *
             * @type String
             */
            let fields = form_elements
                .filter((e) => {
                // Input elements
                if (e instanceof HTMLInputElement) {
                    // Checkbox element
                    if (e.type.toLowerCase() == 'checkbox') {
                        return e.checked == true;
                    }
                    else {
                        return e.value != '';
                    }
                }
                // Select elements
                else if (e instanceof HTMLSelectElement) {
                    let selected = e.options;
                    return selected[selected.selectedIndex].value != '';
                }
                // Textarea elements
                else if (e instanceof HTMLTextAreaElement) {
                    return e.value != '';
                }
            })
                .map((e) => {
                return e.name;
            })
                .join(',');
            obj.form_field = fields;
            window.dataLayer.push(obj);
            if (_self.config.is_testing) {
                console.log(obj);
            }
        }, { capture: true });
    }
    /**
     * Init form beforeunload event on form enter
     *
     * For Marketo forms
     *
     * @param HTMLFormElement form
     * @param Object          marketo_form
     */
    _init_form_exit_marketo(form, marketo_form) {
        let _self = this, obj = {
            'event': 'form_exit',
            'mkto_form_id': marketo_form.getId(),
            'form_name': form.dataset.formTitleValue,
            'form_url': _self.config.site.href,
            'form_field': '',
            'user_id': '',
            'client_id': _self.config.client_id,
            'munchkin_id': _self.config.munchkin_id
        };
        addEventListener('beforeunload', () => {
            let form_elements = Array.from(form.querySelectorAll('.mktoField'));
            /**
             * Get all non-empty fields and map keys to string
             *
             * @type String
             */
            let fields = form_elements
                .filter((e) => {
                // Input elements
                if (e instanceof HTMLInputElement) {
                    // Checkbox element
                    if (e.type.toLowerCase() == 'checkbox') {
                        return e.checked == true;
                    }
                    else {
                        return e.value != '';
                    }
                }
                // Select elements
                else if (e instanceof HTMLSelectElement) {
                    let selected = e.options;
                    return selected[selected.selectedIndex].value != '';
                }
                // Textarea elements
                else if (e instanceof HTMLTextAreaElement) {
                    return e.value != '';
                }
            })
                .map((e) => {
                return e.name;
            })
                .join(',');
            obj.form_field = fields;
            window.dataLayer.push(obj);
            if (_self.config.is_testing) {
                console.log(obj);
            }
        }, { capture: true });
    }
    /**
     * Init form submit event
     *
     * @return void
     */
    _init_form_submit() {
        let _self = this;
        document.querySelectorAll('form.' + this.config.form_class).forEach((form) => {
            /**
             * Marketo form
             *
             */
            if (_self.config.is_marketo) {
                if (typeof window.MktoForms2 !== 'undefined' && window.MktoForms2.whenReady) {
                    window.MktoForms2.whenReady(function (marketo_form) {
                        let obj = {
                            'event': 'form_submit',
                            'mkto_form_id': marketo_form.getId(),
                            'form_name': form.dataset.formTitleValue,
                            'form_url': _self.config.site.href,
                            'form_field': '',
                            'user_id': '',
                            'client_id': _self.config.client_id,
                            'munchkin_id': _self.config.munchkin_id
                        };
                        marketo_form.onSuccess((values, followUpUrl) => {
                            /**
                             * Get all non-empty fields and map keys to string
                             *
                             * @type String
                             */
                            let fields = Object.entries(values)
                                .filter(([k, v]) => {
                                return (typeof v !== 'undefined' && v);
                            })
                                .map((e) => {
                                return e[0];
                            })
                                .join(',');
                            obj.form_field = fields;
                            window.dataLayer.push(obj);
                            if (_self.config.is_testing) {
                                console.log(obj);
                            }
                        });
                    });
                }
            }
            /**
             * Non-Marketo form
             *
             */
            else {
                let obj = {
                    'event': 'form_submit',
                    'mkto_form_id': '',
                    'form_name': form.dataset.title,
                    'form_url': _self.config.site.href,
                    'form_field': '',
                    'user_id': '',
                    'client_id': _self.config.client_id,
                    'munchkin_id': _self.config.munchkin_id
                };
                form.addEventListener('submit', (e) => {
                    const form_elements = Array.from(form.elements);
                    /**
                     * Get all non-empty fields and map keys to string
                     *
                     * @type String
                     */
                    let fields = form_elements
                        .filter((e) => {
                        // Input elements
                        if (e instanceof HTMLInputElement) {
                            // Checkbox element
                            if (e.type.toLowerCase() == 'checkbox') {
                                return e.checked == true;
                            }
                            else {
                                return e.value != '';
                            }
                        }
                        // Select elements
                        else if (e instanceof HTMLSelectElement) {
                            let selected = e.options;
                            return selected[selected.selectedIndex].value != '';
                        }
                        // Textarea elements
                        else if (e instanceof HTMLTextAreaElement) {
                            return e.value != '';
                        }
                    })
                        .map((e) => {
                        return e.name;
                    })
                        .join(',');
                    obj.form_field = fields;
                    window.dataLayer.push(obj);
                    if (this.config.is_testing) {
                        e.preventDefault();
                        console.log(obj);
                    }
                });
            }
        });
    }
    /**
     * Youtube: Track video start
     *
     * @param  string player_id
     * @param  object YT
     * @param  object player
     * @return void
     */
    track_youtube_video_start(player_id, YT, player) {
        if (YT.PlayerState.PLAYING) {
            let obj = {
                'event': 'video_start',
                'video_title': player.getVideoData().title,
                'video_id': player.getVideoData().video_id,
                'video_provider': 'YouTube',
                'video_url': player.getVideoUrl(),
                'video_duration': player.getDuration(),
                'video_star': '',
            };
            window.dataLayer.push(obj);
            if (this.config.is_testing) {
                console.log(obj);
            }
        }
    }
    /**
     * Youtube: Track video end
     *
     * @param  string player_id
     * @param  object YT
     * @param  object player
     * @return void
     */
    track_youtube_video_end(player_id, YT, player) {
        if (YT.PlayerState.PLAYING) {
            let obj = {
                'event': 'video_end',
                'video_title': player.getVideoData().title,
                'video_id': player.getVideoData().video_id,
                'video_provider': 'YouTube',
                'video_url': player.getVideoUrl(),
                'video_duration': player.getDuration(),
                'video_complete': true,
            };
            window.dataLayer.push(obj);
            if (this.config.is_testing) {
                console.log(obj);
            }
        }
    }
    /**
     * Global page event object
     *
     * @return object
     */
    get global_page() {
        return {
            event: 'page_detail',
            page_detail: {
                page_title: this.globals.page_title,
                page_template: this.globals.page_template,
                page_category: this.globals.page_category,
                page_section: this.globals.page_section,
                published_date: this.globals.published_date,
                author: this.globals.author,
            },
            user_info: {
                user_id: this.config.user_id,
                client_id: this.config.client_id,
                login_status: this.config.is_logged_in,
                segment_id: this.config.segment_id,
                munchkin_id: this.config.munchkin_id,
            }
        };
    }
}
